import React from 'react';
import { Box, Card, Divider, List, ListItemV2, SkeletonLoader, Typography } from '@drone-js/core';
import { Props } from 'component/common/HorizontalCard/HorizontalCard.types';
import { RadiusType } from '@drone-js/theme/dist/types/Theme';
import { Gradient } from '@drone-js/core/dist/types/util/gradient';
import { BackgroundType } from '@drone-js/theme';
import { useSelector } from 'react-redux';
import { selectCmsFromBootstrap, selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import { PoiListItemContainer } from 'component/common/ActivityPOICard/PoiListItem';
import { useInView } from 'react-intersection-observer';
import { ActivityImage } from 'component/common/ActivityImage/ActivityImage';

const CARD_WIDTH = '560px';
const FIGURE_WIDTH = '240px';
const CONTENT_WIDTH = '320px';
const radius: RadiusType[] = ['16', '0', '0', '16'];
const gradient: Gradient<BackgroundType> = {
    stops: [
        {
            backgroundColor: 'base.transparent',
            position: '0%',
        },
        {
            backgroundColor: 'base.overlay.strong',
            position: '64%',
        },
    ],
};

const HorizontalCardLoader: React.FC<{}> = () => {
    const cms = useSelector(selectCmsFromBootstrap);
    const activitiesSkeletonContent = new Array(3).fill(0);
    const skeletonContent = (
        <>
            <Box borderRadius="16">
                <SkeletonLoader label={cms[326495]} shape="circle" width="187px" height="14px" me="12" />
            </Box>
            <Box borderRadius="16">
                <SkeletonLoader label={cms[326495]} shape="circle" width="62px" height="14px" me="12" />
            </Box>
        </>
    );

    return (
        <Card
            type="non-interactive"
            variant="outlined"
            width={CARD_WIDTH}
            display="flex"
            borderRadius="16"
            my="16"
            mx="8"
            overflow="hidden"
            data-testid="horizontal-card-loading-container"
        >
            <Box width={FIGURE_WIDTH}>
                <SkeletonLoader label={cms[326495]} shape="rectangle" width="240px" height="100%" me="12" />
            </Box>
            <Box width={CONTENT_WIDTH} p="4">
                <List p="0">
                    {activitiesSkeletonContent.map((_, index) => (
                        <div key={index}>
                            <ListItemV2 contentSlot={skeletonContent} />
                            {index !== 2 && (
                                <Box mx="24">
                                    <Divider direction="horizontal" />
                                </Box>
                            )}
                        </div>
                    ))}
                </List>
            </Box>
        </Card>
    );
};
HorizontalCardLoader.displayName = 'HorizontalCardLoader';

const HorizontalCard: React.FC<Props> = (props) => {
    const { data, isLoading, onSeen, onHover } = props;
    const isPageContainerSpacing = useSelector(selectFeatureBehaviorFromBootstrap).PageContainerSpacing;

    const { ref } = useInView({
        threshold: 0.75,
        triggerOnce: true,
        onChange: onSeen,
    });

    if (isLoading) {
        return <HorizontalCardLoader />;
    }

    if (data.activities.length === 0 || (!data && !isLoading)) {
        return null;
    }

    return (
        <Card
            type="non-interactive"
            variant="outlined"
            width={CARD_WIDTH}
            display="flex"
            borderRadius="16"
            my={isPageContainerSpacing ? '0' : '16'}
            mt={isPageContainerSpacing ? undefined : '0'}
            mx="8"
            ms={props.index === 0 ? '0' : '8'}
            overflow="hidden"
            data-testid="horizontal-card-container"
            onMouseEnter={onHover}
            ref={ref}
        >
            <Box width={FIGURE_WIDTH}>
                <ActivityImage src={data.image} alt={data.imageAlt} objectFit="cover" width={FIGURE_WIDTH} height="100%" responsive="original" loading="immediate" />
                <Box position="relative" height="100%">
                    <Box
                        width={FIGURE_WIDTH}
                        position="absolute"
                        borderRadius={radius}
                        backgroundColor={gradient}
                        display="flex"
                        flexDirection="column-reverse"
                        height="calc(100% - 32px)" // This value will be equal to (100% - 2 * border radius)
                        top="calc(-100% + 32px)" // This value will be equal to (-100% + 2 * border radius))
                    >
                        <Typography variant="heading-default" textColor="base.inverted" p="24" pt="8" lineClamp={2}>
                            {data.title}
                        </Typography>
                        {data.subTitle && (
                            <Typography variant="body-s" textColor="base.inverted" px="24" p="0">
                                {data.subTitle}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box width={CONTENT_WIDTH} p="2">
                <PoiListItemContainer data={data} />
            </Box>
        </Card>
    );
};

HorizontalCard.displayName = 'HorizontalCard';
export default HorizontalCard;
